/**
 * A row drawn as a card from its column metadata.
 *
 * Seven collections had written a card by hand and the rest stayed tables on a
 * phone, because a card was a second rendering of the same row to write and
 * keep in step. The columns already say what a row holds, so the card is
 * derived: the title column heads it, every other column is a labelled line,
 * and the row's actions sit in the footer.
 *
 * @module components/ui/CollectionCards
 */

import React, { type ReactNode } from "react";

import type { Column } from "./DataTable";
import { EntityCard } from "./EntityCard";
import { RowActionControls, type RowActionsSpec } from "./RowActions";

export interface DerivedCardProps<Row> {
  row: Row;
  columns: readonly Column<Row>[];
  actions?: RowActionsSpec<Row>;
  /** Makes the whole card the way into the row. */
  onOpen?: () => void;
  "data-testid"?: string;
}

function cell<Row>(column: Column<Row>, row: Row): ReactNode {
  return (column.cardRender ?? column.render)(row);
}

/**
 * @example
 * ```tsx
 * <DerivedCard row={programme} columns={columns} actions={actions} />
 * ```
 */
export function DerivedCard<Row>({
  row,
  columns,
  actions = undefined,
  onOpen = undefined,
  "data-testid": testId,
}: DerivedCardProps<Row>): React.ReactElement {
  const shown = columns.filter((column) => column.card !== "hidden");
  const title = shown.find((column) => column.card === "title") ?? shown[0];
  const eyebrow = shown.find((column) => column.card === "eyebrow");
  const footer = shown.filter((column) => column.card === "footer");
  const fields = shown.filter(
    (column) => column !== title && column !== eyebrow && column.card !== "footer",
  );
  const hasFooter = footer.length > 0 || actions !== undefined;

  return (
    <EntityCard
      eyebrow={eyebrow ? cell(eyebrow, row) : undefined}
      title={title ? cell(title, row) : null}
      onOpen={onOpen}
      footer={
        hasFooter ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              {footer.map((column) => (
                <React.Fragment key={column.key}>{cell(column, row)}</React.Fragment>
              ))}
            </div>
            {actions && <RowActionControls row={row} spec={actions} touch />}
          </>
        ) : undefined
      }
      data-testid={testId}
    >
      {fields.length > 0 && (
        <dl className="grid grid-cols-[minmax(0,auto)_minmax(0,1fr)] items-baseline gap-x-4 gap-y-1.5 text-base-content">
          {fields.map((column) => {
            const block = column.card === "block";
            return (
              <React.Fragment key={column.key}>
                {!column.hideHeader && (
                  <dt
                    className={`text-muted ${block ? "col-span-2 mt-1.5 first:mt-0" : ""}`.trim()}
                  >
                    {column.header}
                  </dt>
                )}
                <dd
                  className={`min-w-0 break-words ${column.hideHeader || block ? "col-span-2" : ""}`.trim()}
                >
                  {cell(column, row)}
                </dd>
              </React.Fragment>
            );
          })}
        </dl>
      )}
    </EntityCard>
  );
}
